import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert,
    Breadcrumb,
    Button,
    Card,
    Col,
    Modal,
    Row,
    Skeleton,
    Space,
    Tag,
} from 'antd';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    CopyOutlined,
    GiftOutlined,
    IdcardOutlined,
    InfoCircleOutlined,
    PhoneOutlined,
    TagOutlined,
    UserOutlined,
} from '@ant-design/icons';

import useViewVoucher from './useViewVoucher';
import PageHeader from '../../../components/PageHeader';
import Voucher from '../../../components/Voucher';
import RewardImageModal from './components/RewardImageModal';
import { ThemeContext } from '../../../contexts/ThemeContext';
import { formatCpf, formatPhone } from '../../../utils/Masks';
import { formatDateStringToDateAndTime } from '../../../utils/Conversores';
import { urlHttpGov } from '../../../services/urls';

export default function ViewVoucher() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isLoadingVoucher,
        isLoadingAppUserAndRewardData,
        isSubmiting,
        voucher,
        appUser,
        reward,
        handleConfirmRedemption,
        handleClickViewReward,
        isRewardImageModalVisible,
        handleClickCloseRewardImageModal,
        handleConfirm,
        handleCancel,
        isConfirmRedemptionModalVisible,
        handleCopyCode,
        handleNavigateToAppUser,
    } = useViewVoucher();

    if (isLoadingVoucher || !voucher) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Skeleton active paragraph={{ rows: 2 }} />
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <div
                            style={{
                                background: isDark ? '#141414' : '#ffffff',
                                borderRadius: 14,
                                padding: 20,
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <Skeleton active paragraph={{ rows: 6 }} />
                        </div>
                    </Col>
                    <Col xs={24} lg={12}>
                        <div
                            style={{
                                background: isDark ? '#141414' : '#ffffff',
                                borderRadius: 14,
                                padding: 20,
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <Skeleton active paragraph={{ rows: 6 }} />
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    const isDelivered = voucher.status === 'Resgatado';
    const isExpired = voucher.status === 'Expirado';
    const isPending = voucher.status === 'Emitido';

    const statusTagColor = isDelivered ? 'success' : isExpired ? 'error' : 'processing';

    const cardStyle: React.CSSProperties = {
        background: isDark ? '#191919' : '#ffffff',
        borderRadius: 14,
        border: `1px solid ${isDark ? '#2d2d2d' : '#eaedf1'}`,
        boxShadow: isDark
            ? '0 4px 14px rgba(0, 0, 0, 0.35)'
            : '0 2px 10px rgba(0, 0, 0, 0.03)',
    };

    const itemBoxStyle: React.CSSProperties = {
        background: isDark ? '#141414' : '#f8fafc',
        border: `1px solid ${isDark ? '#262626' : '#f1f5f9'}`,
        borderRadius: 10,
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    };

    const labelStyle: React.CSSProperties = {
        fontSize: 11.5,
        fontWeight: 600,
        color: isDark ? '#94a3b8' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    };

    const valueStyle: React.CSSProperties = {
        fontSize: 13.5,
        fontWeight: 600,
        color: isDark ? '#f1f5f9' : '#1e293b',
        lineHeight: 1.4,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Breadcrumb Bar */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/vouchers">Vouchers</Link>,
                        },
                        {
                            title: `Voucher #${voucher.redemption_code}`,
                        },
                    ]}
                />
            </div>

            {/* Page Header */}
            <PageHeader
                icon={<TagOutlined />}
                title={`Voucher #${voucher.redemption_code}`}
                subtitle={
                    <span>
                        <strong>Cidadão:</strong> {appUser?.name || '--'}
                        {voucher.created_at && (
                            <>
                                {' '}• <strong>Solicitado em:</strong> {formatDateStringToDateAndTime(voucher.created_at)}
                            </>
                        )}
                    </span>
                }
                tag={
                    <Tag
                        color={statusTagColor}
                        style={{
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: 8,
                            fontSize: 13,
                        }}
                    >
                        {voucher.status}
                    </Tag>
                }
                actions={
                    <Space wrap>
                        <Button
                            icon={<CopyOutlined />}
                            onClick={handleCopyCode}
                            style={{
                                borderRadius: 8,
                                fontWeight: 500,
                                height: 38,
                            }}
                        >
                            Copiar Código
                        </Button>

                        {appUser?.id && (
                            <Button
                                icon={<UserOutlined />}
                                onClick={handleNavigateToAppUser}
                                style={{
                                    borderRadius: 8,
                                    fontWeight: 500,
                                    height: 38,
                                }}
                            >
                                Perfil do Cidadão
                            </Button>
                        )}

                        {isPending && (
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                onClick={handleConfirmRedemption}
                                loading={isSubmiting}
                                style={{
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    height: 38,
                                    background: '#10b981',
                                    borderColor: '#10b981',
                                }}
                            >
                                Confirmar Entrega
                            </Button>
                        )}
                    </Space>
                }
            />

            {/* Status Contextual Alert Banner */}
            {isPending && (
                <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    message="Voucher Pronto para Entrega"
                    description="Este voucher foi emitido pelo cidadão e está válido aguardando a retirada do produto. Ao realizar a entrega, clique no botão 'Confirmar Entrega'."
                    style={{
                        borderRadius: 10,
                        border: '1px solid #91caff',
                    }}
                />
            )}

            {isDelivered && (
                <Alert
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    message="Voucher Resgatado com Sucesso"
                    description={`A entrega desta recompensa foi confirmada em ${
                        voucher.date_redeemed ? formatDateStringToDateAndTime(voucher.date_redeemed) : '--'
                    }${voucher.panel_user?.name ? ` pelo atendente ${voucher.panel_user.name}` : ''}.`}
                    style={{
                        borderRadius: 10,
                        border: '1px solid #b7eb8f',
                    }}
                />
            )}

            {isExpired && (
                <Alert
                    type="error"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    message="Voucher Expirado"
                    description={`O prazo de validade deste voucher encerrou em ${formatDateStringToDateAndTime(
                        voucher.validity
                    )}. Não é mais permitida a confirmação de entrega.`}
                    style={{
                        borderRadius: 10,
                        border: '1px solid #ffccc7',
                    }}
                />
            )}

            {/* Main Content Grid */}
            <Row gutter={[16, 16]} align="stretch">
                {/* Left Column: Visual Voucher Ticket + Citizen Info */}
                <Col xs={24} lg={12} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Visual Voucher Ticket Card */}
                    {appUser && reward && (
                        <Row>
                            <Voucher
                                voucher={voucher}
                                user={appUser}
                                reward={reward}
                                clickable={false}
                                fullWidth
                            />
                        </Row>
                    )}

                    {/* Citizen Info Card */}
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700 }}>
                                <UserOutlined style={{ color: '#1677ff' }} />
                                Dados do Cidadão
                            </div>
                        }
                        extra={
                            appUser?.id && (
                                <Button
                                    type="link"
                                    onClick={handleNavigateToAppUser}
                                    style={{ padding: 0, fontWeight: 600 }}
                                >
                                    Ver Histórico Completo
                                </Button>
                            )
                        }
                        style={cardStyle}
                        bodyStyle={{ padding: '18px 20px' }}
                    >
                        <Skeleton loading={isLoadingAppUserAndRewardData} active paragraph={{ rows: 3 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                                <div style={{ ...itemBoxStyle, gridColumn: '1 / -1' }}>
                                    <span style={labelStyle}>Nome Completo</span>
                                    <span style={{ ...valueStyle, fontSize: 14.5 }}>
                                        {appUser?.name || '--'}
                                    </span>
                                </div>

                                <div style={itemBoxStyle}>
                                    <span style={labelStyle}>CPF</span>
                                    <span style={{ ...valueStyle, fontFamily: 'monospace' }}>
                                        {appUser?.cpf ? formatCpf(appUser.cpf) : '--'}
                                    </span>
                                </div>

                                <div style={itemBoxStyle}>
                                    <span style={labelStyle}>Telefone</span>
                                    <span style={{ ...valueStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <PhoneOutlined style={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                                        {appUser?.phone ? formatPhone(appUser.phone) : '--'}
                                    </span>
                                </div>

                                <div style={itemBoxStyle}>
                                    <span style={labelStyle}>ID do Cidadão</span>
                                    <span style={{ ...valueStyle, fontFamily: 'monospace' }}>
                                        #{appUser?.id || '--'}
                                    </span>
                                </div>

                                <div style={itemBoxStyle}>
                                    <span style={labelStyle}>Cadastro no App</span>
                                    <span style={valueStyle}>
                                        {appUser?.created_at ? formatDateStringToDateAndTime(appUser.created_at) : '--'}
                                    </span>
                                </div>
                            </div>
                        </Skeleton>
                    </Card>
                </Col>

                {/* Right Column: Reward Info + Audit / Delivery Info */}
                <Col xs={24} lg={12} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Reward Details Card */}
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700 }}>
                                <GiftOutlined style={{ color: '#1677ff' }} />
                                Detalhes da Recompensa
                            </div>
                        }
                        style={cardStyle}
                        bodyStyle={{ padding: '18px 20px' }}
                    >
                        <Skeleton loading={isLoadingAppUserAndRewardData} active paragraph={{ rows: 4 }}>
                            <Row gutter={[16, 16]} align="middle">
                                {reward?.image && (
                                    <Col xs={24} sm={8}>
                                        <div
                                            onClick={handleClickViewReward}
                                            style={{
                                                cursor: 'pointer',
                                                borderRadius: 10,
                                                overflow: 'hidden',
                                                border: `1px solid ${isDark ? '#2d2d2d' : '#eaedf1'}`,
                                                background: isDark ? '#141414' : '#f8fafc',
                                                padding: 8,
                                                textAlign: 'center',
                                                transition: 'transform 0.2s ease',
                                            }}
                                        >
                                            <img
                                                src={`${urlHttpGov}/image_reward/${reward.image}`}
                                                alt={reward.name}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: 120,
                                                    objectFit: 'contain',
                                                    borderRadius: 6,
                                                }}
                                            />
                                        </div>
                                    </Col>
                                )}

                                <Col xs={24} sm={reward?.image ? 16 : 24}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <div>
                                            <div style={labelStyle}>
                                                Produto
                                            </div>
                                            <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', marginTop: 2 }}>
                                                {reward?.name || '--'}
                                            </div>
                                        </div>

                                        <div>
                                            <Tag
                                                color="blue"
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    padding: '2px 10px',
                                                    borderRadius: 6,
                                                }}
                                            >
                                                {(voucher.points_used || 0).toLocaleString('pt-BR')} Pontos
                                            </Tag>
                                        </div>

                                        {reward?.description && (
                                            <div
                                                style={{
                                                    fontSize: 12.5,
                                                    color: isDark ? '#cbd5e1' : '#475569',
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {reward.description}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Skeleton>
                    </Card>

                    {/* Audit & Registration Card */}
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700 }}>
                                <ClockCircleOutlined style={{ color: '#1677ff' }} />
                                Informações do Atendimento & Auditoria
                            </div>
                        }
                        style={cardStyle}
                        bodyStyle={{ padding: '18px 20px' }}
                    >
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                            <div style={itemBoxStyle}>
                                <span style={labelStyle}>Código do Voucher</span>
                                <span style={{ ...valueStyle, fontFamily: 'monospace', color: '#1677ff', fontSize: 15 }}>
                                    {voucher.redemption_code}
                                </span>
                            </div>

                            <div style={itemBoxStyle}>
                                <span style={labelStyle}>ID do Registro</span>
                                <span style={{ ...valueStyle, fontFamily: 'monospace' }}>
                                    #{voucher.id}
                                </span>
                            </div>

                            <div style={itemBoxStyle}>
                                <span style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <ClockCircleOutlined /> Emissão / Solicitação
                                </span>
                                <span style={valueStyle}>
                                    {voucher.created_at ? formatDateStringToDateAndTime(voucher.created_at) : '--'}
                                </span>
                            </div>

                            <div style={itemBoxStyle}>
                                <span style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <CalendarOutlined /> Prazo de Validade
                                </span>
                                <span style={{ ...valueStyle, color: isExpired ? '#ef4444' : undefined, fontWeight: isExpired ? 700 : 600 }}>
                                    {voucher.validity ? formatDateStringToDateAndTime(voucher.validity) : '--'}
                                </span>
                            </div>

                            <div style={{ ...itemBoxStyle, gridColumn: '1 / -1' }}>
                                <span style={labelStyle}>Data da Entrega</span>
                                <div>
                                    {voucher.date_redeemed ? (
                                        <Tag color="success" style={{ fontWeight: 700, fontSize: 12.5, padding: '3px 10px', borderRadius: 6 }}>
                                            {formatDateStringToDateAndTime(voucher.date_redeemed)}
                                        </Tag>
                                    ) : (
                                        <span style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 13 }}>
                                            Aguardando confirmação de entrega
                                        </span>
                                    )}
                                </div>
                            </div>

                            {voucher.panel_user?.name && (
                                <div style={{ ...itemBoxStyle, gridColumn: '1 / -1' }}>
                                    <span style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <IdcardOutlined /> Atendente Responsável
                                    </span>
                                    <span style={{ ...valueStyle, color: isDark ? '#e2e8f0' : '#334155' }}>
                                        {voucher.panel_user.name}
                                    </span>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Reward Image Modal */}
            <RewardImageModal
                isVisible={isRewardImageModalVisible}
                onOk={handleClickCloseRewardImageModal}
                reward={reward}
            />

            {/* Delivery Confirmation Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700 }}>
                        <CheckCircleOutlined style={{ color: '#10b981' }} />
                        Confirmar Entrega de Recompensa
                    </div>
                }
                open={isConfirmRedemptionModalVisible}
                onOk={handleConfirm}
                okButtonProps={{
                    loading: isSubmiting,
                    style: { background: '#10b981', borderColor: '#10b981', fontWeight: 600 },
                }}
                cancelButtonProps={{
                    disabled: isSubmiting,
                }}
                okText="Sim, Confirmar Entrega"
                cancelText="Cancelar"
                onCancel={handleCancel}
                centered
                width={500}
            >
                <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <p style={{ margin: 0, fontSize: 14, color: isDark ? '#e2e8f0' : '#334155' }}>
                        Deseja confirmar a entrega da recompensa correspondente a este voucher?
                    </p>

                    <div
                        style={{
                            background: isDark ? '#141414' : '#f8fafc',
                            borderRadius: 10,
                            padding: '12px 16px',
                            border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            fontSize: 13,
                        }}
                    >
                        <div>
                            <strong>Cidadão:</strong> {appUser?.name}
                        </div>
                        <div>
                            <strong>CPF:</strong> {appUser?.cpf ? formatCpf(appUser.cpf) : '--'}
                        </div>
                        <div>
                            <strong>Recompensa:</strong> {reward?.name}
                        </div>
                        <div>
                            <strong>Código do Voucher:</strong>{' '}
                            <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#1677ff' }}>
                                {voucher.redemption_code}
                            </span>
                        </div>
                    </div>

                    <Alert
                        type="warning"
                        showIcon
                        message="Atenção: Esta ação registrará a entrega no sistema com seu usuário e data/hora atual e não poderá ser desfeita."
                    />
                </div>
            </Modal>
        </div>
    );
}
